import React, { useState } from 'react';
import { AlertTriangle, Shield, X, Check, Heart, Sparkles, Truck, ShoppingCart, Users, Car } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Non-Medical Services Disclaimer/Waiver Component
 * Displays prominently that NC Help Hub is NOT a home health agency
 */
export const NonMedicalDisclaimer = ({ variant = 'banner', onAccept, showCheckbox = false }) => {
  const [accepted, setAccepted] = useState(false);

  // Services we DO provide - The 6 core NC Help Hub services
  const servicesWeProvide = [
    { icon: Sparkles, text: 'Cleaning', desc: 'Home, office, deep cleaning' },
    { icon: Heart, text: 'Laundry', desc: 'Wash, fold, dry cleaning' },
    { icon: Truck, text: 'Courier', desc: 'Local deliveries, packages' },
    { icon: ShoppingCart, text: 'Errands', desc: 'Shopping, pickups, tasks' },
    { icon: Users, text: 'Tasks', desc: 'Odd jobs, organizing, help' },
    { icon: Car, text: 'Rides', desc: 'Transportation services' },
  ];

  // Services we DO NOT provide
  const servicesWeDoNotProvide = [
    'Nursing care or medical treatments',
    'Medication administration (reminders only)',
    'Wound care or injections',
    'Physical therapy or rehabilitation',
    'CNA, LPN, or RN services',
    'Hospice or skilled nursing care',
    'Medical diagnosis or advice',
    'Any clinical or licensed medical services',
  ];

  // Full waiver for booking flow
  if (variant === 'waiver') {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-300 rounded-2xl p-6 mb-6">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-amber-800 mb-1">
              IMPORTANT: Non-Medical Services Acknowledgment
            </h3>
            <p className="text-amber-700 text-sm">
              Please read and acknowledge before proceeding
            </p>
          </div>
        </div>

        <div className="bg-[var(--background-paper)]/80 rounded-xl p-5 mb-4">
          <p className="text-[var(--text-primary)] font-semibold mb-3">
            NC Help Hub LLC is <span className="text-[var(--error)] underline">NOT</span> a Home Health Agency, 
            Medical Provider, or Licensed Healthcare Facility.
          </p>
          <p className="text-[var(--text-primary)] text-sm mb-4">
            We provide non-medical support services only. Our workers are trained helpers, 
            not licensed medical professionals. We do not diagnose, treat, or provide any 
            form of medical or clinical care.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            {/* What we DO provide */}
            <div className="bg-[var(--success)]/10 rounded-lg p-4">
              <h4 className="font-bold text-[var(--success)] mb-2 flex items-center gap-2">
                <Check className="w-4 h-4" /> Services We Provide:
              </h4>
              <ul className="text-sm text-green-700 space-y-1">
                <li>• Cleaning & housekeeping</li>
                <li>• Laundry & dry cleaning</li>
                <li>• Courier & delivery services</li>
                <li>• Errands & shopping</li>
                <li>• General tasks & odd jobs</li>
                <li>• Furniture assembly & organizing</li>
                <li>• Yard work & moving help</li>
              </ul>
            </div>

            {/* What we DO NOT provide */}
            <div className="bg-[var(--error)]/10 rounded-lg p-4">
              <h4 className="font-bold text-[var(--error)] mb-2 flex items-center gap-2">
                <X className="w-4 h-4" /> Services We DO NOT Provide:
              </h4>
              <ul className="text-sm text-red-700 space-y-1">
                {servicesWeDoNotProvide.slice(0, 8).map((item, idx) => (
                  <li key={`excluded-service-${idx}`}>• {item}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              <strong>Need Medical Care?</strong> Please contact a licensed home health agency, 
              your primary care physician, or call 911 for emergencies.
            </p>
          </div>
        </div>

        {showCheckbox && (
          <label className="flex items-start gap-3 cursor-pointer bg-[var(--background-paper)] rounded-lg p-4 border-2 border-[var(--warning)]/30 hover:border-amber-400 transition">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => {
                setAccepted(e.target.checked);
                onAccept && onAccept(e.target.checked);
              }}
              className="mt-1 w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <span className="font-semibold text-[var(--text-primary)]">
                I understand and acknowledge that NC Help Hub provides NON-MEDICAL services only. *
              </span>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                I confirm that I am not seeking medical, nursing, or clinical care. I understand that 
                NC Help Hub workers are not licensed healthcare providers and cannot perform any 
                medical procedures or treatments.
              </p>
            </div>
          </label>
        )}
      </div>
    );
  }

  // Banner variant for landing page
  if (variant === 'banner') {
    return (
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-3 md:py-3 md:px-4">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3 text-center">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0" />
            <span className="font-semibold text-sm md:text-base">
              NOT a Home Health Agency
            </span>
          </div>
          <span className="hidden md:inline text-amber-100">|</span>
          <span className="text-xs md:text-sm text-amber-100 md:text-white">
            Non-medical services only: cleaning, laundry, courier, errands, tasks & rides
          </span>
        </div>
      </div>
    );
  }

  // Card variant for dashboard
  if (variant === 'card') {
    return (
      <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-[var(--warning)]/30 rounded-xl p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h4 className="font-bold text-amber-800">Non-Medical Services Only</h4>
            <p className="text-amber-700 text-xs">
              NC Help Hub is NOT a home health agency
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          {servicesWeProvide.map((service, idx) => (
            <div key={`service-${service.text}-${idx}`} className="bg-[var(--background-paper)] rounded-lg p-2 text-center">
              <service.icon className="w-5 h-5 mx-auto mb-1 text-amber-600" />
              <p className="text-xs font-medium text-[var(--text-primary)]">{service.text}</p>
            </div>
          ))}
        </div>
        
        <p className="text-xs text-amber-700 mt-3 text-center">
          No nursing, medical treatments, or clinical care provided
        </p>
      </div>
    );
  }

  // Compact inline variant
  return (
    <div className="flex items-center gap-2 bg-[var(--warning)]/10 border border-[var(--warning)]/30 rounded-lg px-3 py-2 text-sm">
      <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
      <span className="text-amber-800">
        <strong>Non-Medical Services Only</strong> - No nursing or clinical care
      </span>
    </div>
  );
};

/**
 * Full-page Non-Medical Services Agreement Modal
 */
export const NonMedicalAgreementModal = ({ isOpen, onClose, onAccept }) => {
  const [accepted, setAccepted] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--background-paper)] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-amber-500 text-white p-6 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8" />
            <div>
              <h2 className="text-2xl font-bold">Service Agreement</h2>
              <p className="text-amber-100">Non-Medical Services Acknowledgment</p>
            </div>
          </div>
        </div>

        <div className="p-6">
          <NonMedicalDisclaimer 
            variant="waiver" 
            showCheckbox={true}
            onAccept={setAccepted}
          />

          <div className="flex gap-3 mt-6">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={() => accepted && onAccept()}
              disabled={!accepted}
              className={`flex-1 ${accepted ? 'bg-[var(--success)] hover:bg-[var(--success)]/80' : 'bg-gray-300'}`}
            >
              I Understand & Accept
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NonMedicalDisclaimer;
